import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiGetCycles, apiGetCyclePhases, apiGetCycleStats, apiGetCycleTemperatures, apiGetMentals, apiGetSports } from '../services/api';
import PageHeader from '../components/PageHeader';
import PeriodSelector from '../components/PeriodSelector';
import ViewToggle from '../components/ViewToggle';
import CycleEvolutionChart from '../components/CycleEvolutionChart';
import BasalTemperatureChart from '../components/BasalTemperatureChart';
import { ReactComponent as CycleIcon } from '../assets/cycle/cycle_icon.svg';
import { ReactComponent as ReglesIcon } from '../assets/cycle/regles_icon.svg';
import { ReactComponent as OvulationIcon } from '../assets/cycle/ovulation2_icon.svg';
import { ReactComponent as SymptomesIcon } from '../assets/cycle/symptomes_icon.svg';
import { ReactComponent as CalendrierIcon } from '../assets/cycle/calendrier_icon.svg';
import { ReactComponent as EvolutionIcon } from '../assets/common/evolution_icon.svg';
import { ReactComponent as Symptomes2Icon } from '../assets/cycle/symptomes2_icon.svg';
import InfoButton from '../components/InfoButton';
import { SYMPTOM_PHASE_LABELS, SYMPTOM_PHASE_KEYS } from '../constants/symptomsByPhase';
import { DAYS_LABELS } from '../constants/labels';
import { PHASE, PHASE_API_MAP, PHASE_COLORS_MAP, UI } from '../constants/colors';
import SymptomBar from '../components/SymptomBar';
import ErrorBanner from '../components/ErrorBanner';
import { formatTemperature } from '../utils/temperature';
import './Cycle.css';
import { parseISODate, toISODate } from '../utils/formDate';
import { filterWindow, periodWindow } from '../utils/dateRange';
import PeriodPager, { useHorizontalSwipe, usePeriodOffset } from '../components/PeriodPager';
import DayDetailModal from '../components/DayDetailModal';

const MONTHS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

const SYMPTOM_EMOJIS = {
  'Crampes': '😖', 'Maux de tête': '🤕', 'Ballonnements': '😣', 'Fatigue': '🥴',
  'Nausées': '🤢', 'Courbatures': '🔥', 'Gonflement poitrine': '💔', 'Acné': '😞',
  'Constipation': '😕', 'Jambes lourdes': '🦵',
};
const SYMPTOM_COLORS = [PHASE.regles, PHASE.ovulation, PHASE.luteale, PHASE.regles, PHASE.ovulation, PHASE.luteale, PHASE.regles, PHASE.ovulation, PHASE.luteale, PHASE.regles];

// Format helpers for display
const formatMenstruationRange = (start, end) => {
  if (!start) return null;
  const s = new Date(start);
  const e = end ? new Date(end) : s;
  return `${s.getDate()}-${e.getDate()} ${MONTHS[s.getMonth()].toLowerCase()}`;
};

// Bornes du cycle affiché par la courbe de température. Contrairement à
// `formatMenstruationRange`, les deux dates peuvent tomber sur des mois différents —
// un cycle enjambe presque toujours un changement de mois.
const formatCycleRange = (start, end) => {
  if (!start || !end) return null;
  const s = new Date(start);
  const e = new Date(end);
  const month = (d) => MONTHS[d.getMonth()].toLowerCase();
  return s.getMonth() === e.getMonth()
    ? `${s.getDate()}–${e.getDate()} ${month(e)}`
    : `${s.getDate()} ${month(s)} – ${e.getDate()} ${month(e)}`;
};

const formatOvulationRange = (ovDate) => {
  if (!ovDate) return null;
  const ov = new Date(ovDate);
  const before = new Date(ov); before.setDate(before.getDate() - 1);
  const after = new Date(ov); after.setDate(after.getDate() + 1);
  return `${before.getDate()}-${after.getDate()} ${MONTHS[ov.getMonth()].toLowerCase()}`;
};

// Mois abrégés (« 28 sept. ») : en toutes lettres et en gras, « 28 Septembre – 2 Octobre »
// passait sur deux lignes et la carte « Prochaine phase » changeait de hauteur d'une
// phase à l'autre.
const SHORT_DATE = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short' });

const formatPhaseRange = (dateFrom, dateTo) => {
  const f = parseISODate(dateFrom);
  if (!f) return null;
  const t = parseISODate(dateTo) ?? f;
  return `${SHORT_DATE.format(f)} – ${SHORT_DATE.format(t)}`;
};

/** « En cours », « Demain » ou « Dans 3 jours » : le délai avant le début d'une phase. */
const formatStartsIn = (days) => {
  if (days == null) return '—';
  if (days <= 0) return 'En cours';
  if (days === 1) return 'Demain';
  return `Dans ${days} jours`;
};

const PHASE_LABELS = {
  regles: 'Règles', folliculaire: 'Folliculaire', ovulation: 'Ovulation', luteale: 'Lutéale',
};
const FLUX_LABELS = { Spotting: 'Spotting', Faible: 'Faible', Moyen: 'Moyen', Abondant: 'Abondant' };
const SOMMEIL_LABELS = { 'Bien dormi': 'Bien dormi', 'Réveils fréquents': 'Réveils fréquents', "Difficulté à s'endormir": "Difficulté à s'endormir" };

const Cycle = () => {
  const navigate = useNavigate();
  const today = new Date();
  const todayStr = toISODate(today);
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [cycles, setCycles] = useState([]);
  // Le modal d'une journée aperçoit les trois domaines : sans eux, un jour portant
  // du mental ou du sport mais aucun relevé de cycle s'affichait comme vide.
  const [mentals, setMentals] = useState([]);
  const [sports, setSports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [phases, setPhases] = useState({});
  const [temperatures, setTemperatures] = useState(null);
  const [stats, setStats] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);

  const [phaseIndex, setPhaseIndex] = useState(0);
  const [groupByPhase, setGroupByPhase] = useState(true);

  const [symptomsRangeDays, setSymptomsRangeDays] = useState(() => {
    const v = Number(window.localStorage.getItem('period:cycle:symptoms'));
    return Number.isFinite(v) && v > 0 ? v : 30;
  });
  // Période affichée : 0 = celle qui finit aujourd'hui, 1 = la précédente… (cf. PeriodPager).
  const [symptomsOffset, setSymptomsOffset] = usePeriodOffset(symptomsRangeDays);

  // Cible du clic sur la carte Symptômes : le chiffre du carré ne dit pas *quels*
  // symptômes il compte, le widget du bas si.
  const symptomsWidgetRef = useRef(null);
  // Carrousel de la carte « Prochaine phase » : la phase affichée suit le défilement.
  const phaseTrackRef = useRef(null);

  // Cycle dont on affiche la courbe de température : 0 = en cours, 1 = le précédent.
  // L'endpoint coupait au J1 du cycle courant, sans paramètre : les courbes passées
  // étaient collectées et inatteignables.
  const [tempCycleOffset, setTempCycleOffset] = useState(0);
  // Glisser sur la courbe change de cycle, comme les flèches de l'en-tête : vers la
  // droite, le précédent ; vers la gauche, on revient vers le cycle en cours.
  const bbtSwipe = useHorizontalSwipe({
    onSwipeRight: () => { if (temperatures?.has_previous) setTempCycleOffset((o) => o + 1); },
    onSwipeLeft: () => setTempCycleOffset((o) => Math.max(0, o - 1)),
  });
  // Dernier offset réellement demandé au serveur, pour n'appeler que sur changement.
  const fetchedTempOffset = useRef(0);

  const { user, getToken } = useAuth();

  const loadCycleData = useCallback(() => {
    if (!user?.id) { setLoading(false); return; }
    const token = getToken();
    setLoading(true);
    setError(null);
    Promise.all([
      apiGetCycles(user.id, token),
      apiGetCyclePhases(user.id, token).catch(() => []),
      apiGetCycleStats(user.id, token, symptomsRangeDays).catch(() => null),
      apiGetMentals(user.id, token).catch(() => []),
      apiGetSports(user.id, token).catch(() => []),
      apiGetCycleTemperatures(user.id, token).catch(() => null),
    ]).then(([cycleData, phaseData, statsData, mentalData, sportData, tempData]) => {
      setCycles(cycleData);
      setMentals(mentalData || []);
      setSports(sportData || []);
      // Ce chargement ramène toujours le cycle en cours : on recale la navigation
      // dessus, sinon les flèches et le sous-titre annonceraient un cycle passé sur
      // la courbe du cycle courant.
      setTemperatures(tempData);
      fetchedTempOffset.current = 0;
      setTempCycleOffset(0);
      const lookup = {};
      // Le backend renvoie désormais une entrée par jour calendaire (et non par
      // relevé) : le calendrier n'a plus de trou, et `estimation` distingue les
      // jours déduits des jours confirmés par un signal biologique.
      (phaseData || []).forEach(p => {
        lookup[String(p.date)] = {
          phase: PHASE_API_MAP[p.phase] || p.phase.toLowerCase(),
          estimation: p.estimation,
          cycleDay: p.cycle_day,
          methode: p.methode,
          confiance: p.confiance,
        };
      });
      setPhases(lookup);
      setStats(statsData);
    }).catch(() => setError('Impossible de charger tes données. Vérifie ta connexion et réessaie.'))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, getToken]);

  useEffect(() => {
    loadCycleData();
  }, [loadCycleData]);

  // Changement de cycle affiché sur la courbe de température. Même garde que ci-dessus :
  // le chargement initial a déjà ramené l'offset 0.
  useEffect(() => {
    if (!user?.id) return;
    if (fetchedTempOffset.current === tempCycleOffset) return;
    const requested = tempCycleOffset;
    fetchedTempOffset.current = requested;
    apiGetCycleTemperatures(user.id, getToken(), requested)
      // Le serveur ramène un offset trop grand au plus ancien cycle : on se recale sur
      // l'offset qu'il a réellement servi, sans quoi les flèches se désynchroniseraient
      // de la courbe affichée.
      .then((series) => {
        setTemperatures(series);
        if (series?.cycle_offset != null && series.cycle_offset !== requested) {
          fetchedTempOffset.current = series.cycle_offset;
          setTempCycleOffset(series.cycle_offset);
        }
      })
      .catch(() => {});
  }, [tempCycleOffset, user?.id, getToken]);

  const cycleByDate = Object.fromEntries(cycles.map(c => [c.date, c]));
  const mentalByDate = Object.fromEntries(mentals.map(m => [m.date, m]));
  const sportByDate = Object.fromEntries(sports.map(sp => [sp.date, sp]));

  const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();
  const offset = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();

  const prevMonth = () => {
    if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(currentYear - 1); }
    else setCurrentMonth(currentMonth - 1);
  };
  const isAtCurrentMonth = currentYear === today.getFullYear() && currentMonth >= today.getMonth();
  const nextMonth = () => {
    if (isAtCurrentMonth) return;
    if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(currentYear + 1); }
    else setCurrentMonth(currentMonth + 1);
  };

  const calendarCells = [];
  for (let i = 0; i < offset; i++) calendarCells.push(null);
  for (let d = 1; d <= daysInMonth; d++) calendarCells.push(d);

  // Derived display values from backend stats
  const lastCycleDuration = stats?.last_cycle_duration ?? null;
  const lastMenstruationDays = stats?.last_menstruation_days ?? 0;
  const lastMenstruationDates = formatMenstruationRange(stats?.last_menstruation_start, stats?.last_menstruation_end);
  const bbtCycleLabel = formatCycleRange(temperatures?.j1, temperatures?.cycle_end);
  const lastOvulationDay = stats?.last_ovulation_day ?? null;
  const lastOvulationDateRange = formatOvulationRange(stats?.last_ovulation_date);
  const ovulationPassed = stats?.last_ovulation_date != null && stats.last_ovulation_date <= todayStr;
  const symptomsThisMonth = stats?.symptoms_this_month ?? 0;
  const avgCycleDuration = stats?.avg_cycle_duration != null ? Math.round(Number(stats.avg_cycle_duration)) : null;

  // Jour actuel dans le cycle courant (depuis le début des dernières règles)
  const currentCycleDay = stats?.last_menstruation_start
    ? Math.floor((new Date(todayStr) - new Date(stats.last_menstruation_start)) / (1000 * 60 * 60 * 24)) + 1
    : null;
  const evolutionData = stats?.evolution_data ?? [];
  const nextPeriodDate = parseISODate(stats?.next_period_date);
  const daysUntilNext = stats?.days_until_next_period ?? null;
  const predictionConfidence = stats?.prediction_confidence ?? null;

  const PHASE_LABEL_COLORS = {
    'Règles':       PHASE.regles,
    'Folliculaire': PHASE.folliculaire,
    'Ovulation':    PHASE.ovulation,
    'Lutéale':      PHASE.luteale,
  };
  // Le moteur renvoie la fin du cycle en cours puis tout le cycle suivant (jusqu'à 8
  // phases) ; la carte s'arrête aux 4 prochaines — un tour de cycle.
  const futurePhases = (stats?.future_phases ?? []).slice(0, 4).map(fp => ({
    label: fp.label,
    color: PHASE_LABEL_COLORS[fp.label] ?? PHASE.regles,
    dateRange: formatPhaseRange(fp.date_from, fp.date_to),
    daysFrom: fp.days_from_today,
  }));

  const nextOvulationPhase = (stats?.future_phases ?? []).find(fp => fp.label === 'Ovulation');
  // Mois abrégé (« 28 oct. ») : en grand chiffre dans une carte carrée de 143 px, le
  // mois en toutes lettres ne tenait pas sur une ligne.
  const nextOvulationDate = parseISODate(nextOvulationPhase?.date_from);
  const nextOvulationDateStr = nextOvulationDate ? SHORT_DATE.format(nextOvulationDate) : null;

  // Calculée ici et non plus par l'API (`symptom_list` des stats, qui ne connaît que
  // « les N derniers jours ») : la période peut maintenant remonter dans le passé, et
  // tout l'historique des relevés est déjà chargé. Même règle que le serveur : jours
  // avec le symptôme sur la période, rapportés à sa durée ; référence sur 3 périodes.
  const earliestCycle = useMemo(
    () => cycles.reduce((min, c) => (min == null || c.date < min ? c.date : min), null),
    [cycles]
  );
  const symptomsWindow = useMemo(
    () => periodWindow(symptomsRangeDays, symptomsOffset),
    [symptomsRangeDays, symptomsOffset]
  );
  const symptomList = useMemo(() => {
    const countIn = (records) => {
      const freq = {};
      records.forEach((c) => (c.physical_symptoms ?? []).forEach((label) => {
        freq[label] = (freq[label] ?? 0) + 1;
      }));
      return freq;
    };
    const freq = countIn(filterWindow(cycles, symptomsWindow, (c) => c.date));
    const triple = countIn(filterWindow(
      cycles,
      periodWindow(symptomsRangeDays, symptomsOffset, symptomsRangeDays * 3),
      (c) => c.date,
    ));
    return Object.entries(freq)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([label, days], i) => ({
        emoji: SYMPTOM_EMOJIS[label] || '🔸',
        label,
        days,
        color: SYMPTOM_COLORS[i % SYMPTOM_COLORS.length],
        pct: Math.min(100, (days / symptomsRangeDays) * 100),
        avgPct: Math.min(100, ((triple[label] ?? 0) / (symptomsRangeDays * 3)) * 100),
      }));
  }, [cycles, symptomsWindow, symptomsRangeDays, symptomsOffset]);

  // Regroupement par la phase **du jour où le symptôme a été saisi**.
  //
  // Le regroupement passait auparavant par un catalogue statique symptôme → phases :
  // « Fatigue » y figurant sous Règles, Folliculaire et Lutéale, la saisir une fois
  // pendant les règles la faisait apparaître sous trois phases, dont deux non encore
  // vécues. Des ressentis semblaient donc déjà renseignés dans le futur.
  const symptomsByPhase = useMemo(() => {
    // Même fenêtre que la liste à plat : elle remonte dans le passé avec la période.
    const fromStr = toISODate(symptomsWindow.start);
    const toStr = toISODate(symptomsWindow.end);

    const counts = {};      // phase -> { label -> nb de jours }
    const phaseDays = {};   // phase -> nb de jours de cette phase dans la fenêtre

    Object.entries(phases).forEach(([dateKey, info]) => {
      if (dateKey < fromStr || dateKey > toStr) return;
      phaseDays[info.phase] = (phaseDays[info.phase] ?? 0) + 1;
    });

    cycles.forEach((c) => {
      if (c.date < fromStr || c.date > toStr) return;
      const phase = phases[c.date]?.phase;
      if (!phase) return;
      (c.physical_symptoms ?? []).forEach((label) => {
        counts[phase] = counts[phase] ?? {};
        counts[phase][label] = (counts[phase][label] ?? 0) + 1;
      });
    });

    return { counts, phaseDays };
  }, [cycles, phases, symptomsWindow]);

  return (
    <div className="cycle-page">
      <PageHeader title="Cycle" page="cycle" />

      {error && <ErrorBanner message={error} onRetry={loadCycleData} />}

      {/* Estimation badge */}
      {stats?.is_estimated && (
        <div className="cycle-page__estimation-badge">
          Estimation — s'affine avec un suivi régulier
        </div>
      )}

      {/* Relance de saisie de la température basale : de la glaire fertile a été
          relevée, mais sans série de températures l'ovulation ne peut pas être
          confirmée — la phase reste déduite du calendrier. */}
      {stats?.bbt_prompt && (
        <button
          type="button"
          className="cycle-page__bbt-prompt"
          onClick={() => navigate('/formulaire/cycle')}
        >
          <span className="cycle-page__bbt-prompt-title">🌡️ Confirme ton ovulation</span>
          <span className="cycle-page__bbt-prompt-text">
            Note ta température au réveil quelques matins d'affilée : c'est le seul moyen
            de confirmer ton ovulation. En attendant, ta phase reste estimée.
          </span>
        </button>
      )}

      {/* État vide : aucun relevé de cycle enregistré.
          Les chiffres affichés plus bas sont alors purement théoriques, et rien
          n'indiquait comment saisir ses règles — d'où le « je ne trouve pas
          comment rentrer les données » remonté en bêta. */}
      {!loading && cycles.length === 0 && (
        <div className="cycle-page__empty">
          <p className="cycle-page__empty-title">Aucune donnée de cycle pour l'instant</p>
          <p className="cycle-page__empty-text">
            Les prédictions ci-dessous sont théoriques. Enregistre tes règles pour
            qu'elles se calent sur ton cycle réel.
          </p>
          <button
            className="cycle-page__empty-btn"
            onClick={() => navigate('/formulaire/cycle')}
          >
            Ajouter mes règles
          </button>
          <p className="cycle-page__empty-help">
            Tu peux aussi toucher n'importe quel jour du calendrier pour saisir
            une date passée.
          </p>
        </div>
      )}

      {/* 4 cartes résumé */}
      <div className="cycle-page__cards">
        <div className="cycle-page__card cycle-page__card--blue">
          <div className="cycle-page__card-top">
            <div className="cycle-page__card-icon"><CycleIcon /></div>
            <span className="cycle-page__card-label">Cycle</span>
          </div>
          <span className="cycle-page__card-info"><InfoButton infoKey="cardCycle" /></span>
          <span className="cycle-page__card-value">{currentCycleDay != null ? `J${currentCycleDay}` : '—'}</span>
          <span className="cycle-page__card-sub">du cycle actuel</span>
          <span className="cycle-page__card-avg">moy. {avgCycleDuration != null ? `${avgCycleDuration} j` : '—'}</span>
        </div>
        <div className="cycle-page__card cycle-page__card--pink">
          <div className="cycle-page__card-top">
            <div className="cycle-page__card-icon"><ReglesIcon /></div>
            <span className="cycle-page__card-label">Règles</span>
          </div>
          <span className="cycle-page__card-info"><InfoButton infoKey="cardRegles" /></span>
          <span className="cycle-page__card-value">{lastMenstruationDays > 0 ? `${lastMenstruationDays} j` : '—'}</span>
          <span className="cycle-page__card-sub">{lastMenstruationDates ?? 'aucune donnée'}</span>
          <span className="cycle-page__card-avg">dernières règles</span>
        </div>
        <div className="cycle-page__card cycle-page__card--orange">
          <div className="cycle-page__card-top">
            <div className="cycle-page__card-icon"><OvulationIcon /></div>
            <span className="cycle-page__card-label">Ovulation</span>
          </div>
          <span className="cycle-page__card-info"><InfoButton infoKey="cardOvulation" /></span>
          {ovulationPassed ? (
            <>
              <span className="cycle-page__card-value">{lastOvulationDay ? `J${lastOvulationDay}` : '—'}</span>
              <span className="cycle-page__card-sub">le {lastOvulationDateRange ?? '—'}</span>
              {/* Une ovulation validée par le plateau thermique et une ovulation
                  simplement projetée n'ont pas la même valeur : on le dit. */}
              <span className="cycle-page__card-avg">
                {stats?.ovulation_confirmed ? 'confirmée' : 'estimée'}
              </span>
            </>
          ) : (
            <>
              <span className="cycle-page__card-value">{nextOvulationDateStr ?? '—'}</span>
              <span className="cycle-page__card-sub">prévue pour</span>
              <span className="cycle-page__card-avg">{nextOvulationPhase ? `dans ${nextOvulationPhase.days_from_today} jours` : 'aucune donnée'}</span>
            </>
          )}
        </div>
        {/* Seule carte cliquable de la grille : elle mène au détail de son propre
            chiffre, le widget « Symptômes physiques fréquents » plus bas. */}
        <div
          className="cycle-page__card cycle-page__card--purple cycle-page__card--linked"
          role="button"
          tabIndex={0}
          aria-label="Symptômes — voir le détail"
          onClick={() => symptomsWidgetRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              symptomsWidgetRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }}
        >
          <div className="cycle-page__card-top">
            <div className="cycle-page__card-icon"><SymptomesIcon /></div>
            <span className="cycle-page__card-label">Symptômes</span>
          </div>
          {/* Sans cette coupure, ouvrir l'info déclencherait aussi le défilement. */}
          <span
            className="cycle-page__card-info"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <InfoButton infoKey="cardSymptomes" />
          </span>
          <span className="cycle-page__card-value">{symptomsThisMonth}</span>
          <span className="cycle-page__card-sub">ce mois-ci</span>
          <span className="cycle-page__card-avg">dernier cycle : {lastCycleDuration != null ? `${lastCycleDuration} j` : '—'}</span>
        </div>
      </div>

      {/* Widget Calendrier du cycle */}
      <div className="cycle-page__widget">
        <div className="cycle-page__widget-header">
          <div className="cycle-page__widget-icon cycle-page__widget-icon--cal">
            <CalendrierIcon />
          </div>
          <h3>Calendrier du cycle</h3>
        </div>
        <div className="cycle-page__cal">
          <div className="cycle-page__cal-nav">
            <span className="cycle-page__cal-month">{MONTHS[currentMonth]} {currentYear}</span>
            <div className="cycle-page__cal-arrows">
              <button className="cycle-page__cal-arrow" onClick={prevMonth} aria-label="Mois précédent">&#8249;</button>
              <button className="cycle-page__cal-arrow" onClick={nextMonth} disabled={isAtCurrentMonth} style={isAtCurrentMonth ? { opacity: 0.3, cursor: 'default' } : {}} aria-label="Mois suivant">&#8250;</button>
            </div>
          </div>
          <div className="cycle-page__cal-days">
            {DAYS_LABELS.map((d, i) => <span key={i}>{d}</span>)}
          </div>
          <div className="cycle-page__cal-grid">
            {calendarCells.map((day, i) => {
              if (day === null) return <div key={i} className="cycle-page__cal-cell" />;
              const dateKey = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const entry = cycleByDate[dateKey];
              const isToday = day === today.getDate() && currentMonth === today.getMonth() && currentYear === today.getFullYear();
              
              const phaseInfo = phases[dateKey];
              const phase = phaseInfo ? phaseInfo.phase : null;
              
              // Determine ovulation borders based on phase
              const isOvulation = phase === 'ovulation';
              const isOvulationAdjacent = false;
              
              let numClasses = 'cycle-page__cal-num';
              if (isToday) numClasses += ' cycle-page__cal-num--today';
              else if (isOvulation) numClasses += ' cycle-page__cal-num--ovulation';
              else if (isOvulationAdjacent) numClasses += ' cycle-page__cal-num--ovulation-adjacent';
              
              return (
                <div key={i} className="cycle-page__cal-cell" onClick={() => setSelectedDate(dateKey)} style={{ cursor: 'pointer' }}>
                  <span className={numClasses}>
                    {day}
                  </span>
                  {/* Pastille creuse pour un jour estimé : maintenant que le calendrier
                      est rempli tous les jours, il faut distinguer d'un coup d'œil ce
                      qui est confirmé par un relevé de ce qui est déduit. */}
                  {phase && !isOvulation && (
                    <span
                      className={
                        `cycle-page__cal-dot cycle-page__cal-dot--${phase}` +
                        (phaseInfo?.estimation ? ' cycle-page__cal-dot--estimated' : '')
                      }
                      title={phaseInfo?.estimation ? 'Phase estimée' : 'Phase confirmée par tes relevés'}
                    />
                  )}
                </div>
              );
            })}
          </div>
          <div className="cycle-page__legend">
            <div className="cycle-page__legend-item">
              <span className="cycle-page__legend-dot cycle-page__legend-dot--regles" />
              <span>Règles</span>
            </div>
            <div className="cycle-page__legend-item">
              <span className="cycle-page__legend-dot cycle-page__legend-dot--folliculaire" />
              <span>Folliculaire</span>
            </div>
            <div className="cycle-page__legend-item">
              <span className="cycle-page__legend-dot cycle-page__legend-dot--ovulation" />
              <span>Ovulation</span>
            </div>
            <div className="cycle-page__legend-item">
              <span className="cycle-page__legend-dot cycle-page__legend-dot--luteale" />
              <span>Lutéale</span>
            </div>
            <div className="cycle-page__legend-item">
              <span className="cycle-page__legend-dot cycle-page__legend-dot--estimated" />
              <span>Estimée</span>
            </div>
          </div>
        </div>
      </div>

      {/* Widget Évolution du cycle */}
      <div className="cycle-page__widget">
        <div className="cycle-page__widget-header">
          <div className="cycle-page__widget-icon cycle-page__widget-icon--evo">
            <EvolutionIcon />
          </div>
          <div className="cycle-page__widget-title-group">
            <h3>Évolution du cycle<InfoButton infoKey="dureePhasesChart" /></h3>
            <p className="cycle-page__widget-subtitle">Durée moyenne du cycle : {avgCycleDuration != null ? `${avgCycleDuration} j` : '—'}</p>
          </div>
        </div>
        <CycleEvolutionChart monthlyData={evolutionData} isEstimated={!!stats?.is_estimated} />
      </div>

      {/* Widget Température basale.
          La température était collectée depuis toujours et servait à confirmer
          l'ovulation, sans qu'aucun écran ne la restitue : on demandait une mesure
          quotidienne au thermomètre sans jamais en montrer le résultat. */}
      <div className="cycle-page__widget">
        <div className="cycle-page__widget-header">
          <div className="cycle-page__widget-icon cycle-page__widget-icon--evo">
            <EvolutionIcon />
          </div>
          <div className="cycle-page__widget-title-group">
            <h3>Température basale<InfoButton infoKey="temperatureBasaleChart" /></h3>
            <p className="cycle-page__widget-subtitle">
              {tempCycleOffset > 0
                ? (bbtCycleLabel ?? 'Cycle précédent')
                : temperatures?.ovulation_confirmed
                  ? 'Le plateau haut confirme ton ovulation'
                  : 'Le décalage thermique signale ton ovulation'}
            </p>
          </div>
          {/* La courbe se lit en jours de cycle : on remonte cycle par cycle, là où une
              fenêtre en jours ferait collisionner les J de deux cycles voisins. */}
          <div className="cycle-page__cal-arrows cycle-page__cal-arrows--inline">
            <button
              className="cycle-page__cal-arrow"
              onClick={() => setTempCycleOffset((o) => o + 1)}
              disabled={!temperatures?.has_previous}
              style={!temperatures?.has_previous ? { opacity: 0.3, cursor: 'default' } : {}}
              aria-label="Cycle précédent"
            >&#8249;</button>
            <button
              className="cycle-page__cal-arrow"
              onClick={() => setTempCycleOffset((o) => Math.max(0, o - 1))}
              disabled={tempCycleOffset === 0}
              style={tempCycleOffset === 0 ? { opacity: 0.3, cursor: 'default' } : {}}
              aria-label="Cycle suivant"
            >&#8250;</button>
          </div>
        </div>
        <div className="cycle-page__bbt-swipe" {...bbtSwipe}>
        {temperatures?.points?.length ? (
          <BasalTemperatureChart series={temperatures} />
        ) : tempCycleOffset > 0 ? (
          /* Un cycle passé sans aucun relevé n'est pas une invitation à commencer sa
             courbe : il est trop tard pour le mesurer, seule la navigation a du sens. */
          <p className="cycle-page__bbt-empty">Aucune température relevée sur ce cycle.</p>
        ) : (
          <button
            type="button"
            className="cycle-page__bbt-prompt"
            onClick={() => navigate('/formulaire/cycle')}
          >
            <span className="cycle-page__bbt-prompt-title">🌡️ Commence ta courbe</span>
            <span className="cycle-page__bbt-prompt-text">
              Prends ta température au réveil, avant de te lever. Après quelques matins,
              la courbe montre la hausse qui suit l'ovulation.
            </span>
          </button>
        )}
        </div>
      </div>

      {/* Widget Prochaine [phase].
          Texte en encre, la couleur de la phase réduite à sa pastille et au fond de
          l'icône : en couleur de phase, le titre et les dates de la phase folliculaire
          (vert vif) ou de l'ovulation (ambre) tombaient à 1,4 et 1,9:1 de contraste sur
          blanc. Le titre est fixe et la plage de dates a sa propre ligne : la carte garde
          la même hauteur pour les quatre phases. */}
      {(() => {
        // Sans phase calculée, la date des prochaines règles tient lieu de prochaine phase.
        const fallback = nextPeriodDate
          ? { label: 'Règles', color: PHASE.regles, dateRange: SHORT_DATE.format(nextPeriodDate), daysFrom: daysUntilNext }
          : null;
        const slides = futurePhases.length > 0 ? futurePhases : [fallback];
        const active = Math.min(phaseIndex, slides.length - 1);
        const phase = slides[active];
        const swipeable = slides.length > 1;

        // Défilement natif au doigt, aimanté phase par phase : la phase affichée est
        // celle qui occupe la carte, lue sur la position de défilement.
        const onTrackScroll = (e) => {
          const el = e.currentTarget;
          if (!el.clientWidth) return;
          setPhaseIndex(Math.round(el.scrollLeft / el.clientWidth));
        };
        const goTo = (i) => {
          const el = phaseTrackRef.current;
          if (!el) return;
          const left = i * el.clientWidth;
          if (typeof el.scrollTo === 'function') el.scrollTo({ left, behavior: 'smooth' });
          else el.scrollLeft = left;
          setPhaseIndex(i);
        };

        return (
          <div className="cycle-page__widget cycle-page__prediction">
            <div className="cycle-page__widget-header">
              <div
                className="cycle-page__widget-icon cycle-page__prediction-icon"
                style={phase ? { '--prediction-accent': phase.color } : undefined}
              >
                <CalendrierIcon />
              </div>
              <div className="cycle-page__widget-title-group">
                <h3>Prochaine phase</h3>
                <p className="cycle-page__widget-subtitle">
                  Estimation basée sur votre cycle
                  {predictionConfidence != null && <> · confiance {predictionConfidence} %</>}
                </p>
              </div>
            </div>
            <div
              ref={phaseTrackRef}
              className={`cycle-page__prediction-track${swipeable ? ' cycle-page__prediction-track--swipeable' : ''}`}
              onScroll={swipeable ? onTrackScroll : undefined}
              tabIndex={swipeable ? 0 : undefined}
              role={swipeable ? 'group' : undefined}
              aria-label={swipeable ? 'Prochaines phases, faire glisser pour passer à la suivante' : undefined}
            >
              {slides.map((p, i) => (
                <div
                  key={p ? `${p.label}-${p.dateRange}` : 'vide'}
                  className="cycle-page__prediction-rows cycle-page__prediction-slide"
                  aria-hidden={swipeable && i !== active ? true : undefined}
                >
                  <div className="cycle-page__prediction-phase">
                    {p ? (
                      <span className="cycle-page__prediction-pill" style={{ '--prediction-accent': p.color }}>
                        <span className="cycle-page__prediction-dot" aria-hidden="true" />
                        {p.label}
                      </span>
                    ) : (
                      <span className="cycle-page__prediction-pill cycle-page__prediction-pill--empty">Aucune estimation</span>
                    )}
                    <span className="cycle-page__prediction-dates">
                      {p?.dateRange ?? 'Données insuffisantes'}
                    </span>
                  </div>
                  <div className="cycle-page__prediction-divider" />
                  <div className="cycle-page__prediction-row">
                    <span className="cycle-page__prediction-row-label">Début</span>
                    <span className="cycle-page__prediction-row-value">
                      {formatStartsIn(p?.daysFrom)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            {/* Repère du défilement, et navigation à la souris où l'on ne glisse pas. */}
            {swipeable && (
              <div className="cycle-page__prediction-dots">
                {slides.map((p, i) => (
                  <button
                    key={`${p.label}-${p.dateRange}`}
                    type="button"
                    className={`cycle-page__prediction-dot-btn${i === active ? ' cycle-page__prediction-dot-btn--on' : ''}`}
                    style={{ '--prediction-accent': p.color }}
                    onClick={() => goTo(i)}
                    aria-label={`${p.label}, ${p.dateRange}`}
                    aria-current={i === active ? 'true' : undefined}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* Day detail modal */}
      {selectedDate && (() => {
        const phase = phases[selectedDate]?.phase ?? null;
        return (
          <DayDetailModal
            date={selectedDate}
            entries={{
              cycle: cycleByDate[selectedDate],
              mental: mentalByDate[selectedDate],
              sport: sportByDate[selectedDate],
            }}
            phaseLabel={phase ? (PHASE_LABELS[phase] || phase) : null}
            phaseColor={phase ? (PHASE_COLORS_MAP[phase] || UI.textSecondary) : null}
            isFuture={selectedDate > todayStr}
            onClose={() => setSelectedDate(null)}
          />
        );
      })()}

      {/* Widget Symptômes physiques fréquents */}
      <div className="cycle-page__widget" ref={symptomsWidgetRef}>
        <div className="cycle-page__widget-header">
          <div className="cycle-page__widget-icon cycle-page__widget-icon--sym">
            <Symptomes2Icon />
          </div>
          <div className="cycle-page__widget-title-group">
            <h3>Symptômes physiques fréquents</h3>
            <p className="cycle-page__widget-subtitle">Fréquence des symptômes sur la période</p>
          </div>
          <div className="view-controls">
            <ViewToggle grouped={groupByPhase} onChange={setGroupByPhase} />
            <PeriodSelector
              value={symptomsRangeDays}
              onChange={setSymptomsRangeDays}
              options={[30, 15, 7]}
              storageKey="period:cycle:symptoms"
              className="cycle-page__widget-badge"
              arrowClassName="cycle-page__badge-arrow"
            />
          </div>
        </div>
        <PeriodPager days={symptomsRangeDays} offset={symptomsOffset} onOffsetChange={setSymptomsOffset} earliest={earliestCycle}>
          {symptomList.length === 0 ? (
            <p style={{ textAlign: 'center', color: 'var(--color-text-muted)', padding: '1rem 0', fontSize: '0.85rem' }}>
              Aucun symptôme enregistré sur cette période
            </p>
          ) : groupByPhase ? (
            <div className="phase-groups">
              {SYMPTOM_PHASE_KEYS.map((phaseKey) => {
                const recorded = symptomsByPhase.counts[phaseKey] ?? {};
                const daysInPhase = symptomsByPhase.phaseDays[phaseKey] ?? 0;
                const phaseSymptoms = Object.entries(recorded)
                  .sort((a, b) => b[1] - a[1])
                  .map(([label, days]) => ({
                    emoji: SYMPTOM_EMOJIS[label] || '🔸',
                    label,
                    days,
                    // Fréquence rapportée aux jours de cette phase, et non à la fenêtre
                    // entière : 2 jours de crampes sur 5 jours de règles, c'est 40 %.
                    pct: daysInPhase ? Math.min(100, (days / daysInPhase) * 100) : 0,
                    avgPct: 0,
                  }));
                if (phaseSymptoms.length === 0) return null;
                const phaseColor = PHASE_COLORS_MAP[phaseKey];
                return (
                  <div key={phaseKey} className="phase-bar" style={{ '--phase-bar-color': phaseColor }}>
                    <span className="phase-group-label" style={{ color: phaseColor }}>
                      {SYMPTOM_PHASE_LABELS[phaseKey]}
                    </span>
                    <div className="symptom-bar-list">
                      {phaseSymptoms.map((s, i) => (
                        <SymptomBar
                          key={i}
                          emoji={s.emoji}
                          label={s.label}
                          days={s.days}
                          accentColor={phaseColor}
                          pct={s.pct}
                          avgPct={Math.round(s.avgPct)}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="symptom-bar-list">
              {symptomList.map((s, i) => (
                <SymptomBar
                  key={i}
                  emoji={s.emoji}
                  label={s.label}
                  days={s.days}
                  accentColor={PHASE.ovulation}
                  pct={s.pct}
                  avgPct={Math.round(s.avgPct)}
                />
              ))}
            </div>
          )}
        </PeriodPager>
      </div>
    </div>
  );
};

export default Cycle;
